// ---------------------------------------------------------------------------
// geolocation — friendly wrapper around the location stack that:
//   • Detects iOS / standalone PWA / Capacitor for tailored copy
//   • Reads Permissions API when available (Chrome/Android/desktop)
//   • Routes to Capacitor's native plugin when running inside the iOS/Android
//     shell, and falls back to navigator.geolocation on the web
//   • Translates raw GeolocationPositionError codes into human-readable
//     messages and a stable status string the UI can switch on.
//
// For continuous tracking during live navigation, use watchLocation() from
// `./nativeGeolocation` directly — this file only handles the one-shot
// "where am I right now" case used by the trip-planning screens.
// ---------------------------------------------------------------------------

import type { LngLat } from "@/utils/polyline";
import {
  checkPermission,
  getOneShotLocation,
  isNativeRuntime,
} from "@/utils/nativeGeolocation";

export type GeolocationStatus =
  | "idle"
  | "prompt"        // not asked yet, or browser will show prompt next call
  | "requesting"    // native dialog open / request in flight
  | "granted"
  | "denied"        // user explicitly blocked
  | "unavailable"   // device/browser cannot provide GPS at all
  | "timeout"       // request didn't resolve in time
  | "error";        // anything else (position unavailable, etc.)

export interface GeolocationResult {
  status: GeolocationStatus;
  coords?: LngLat;
  /** m/s when the fix carried it (device in motion) — anticipated-origin
   *  routing uses this to project where a moving driver WILL be. */
  speed?: number | null;
  /** Degrees (0–360) when the fix carried it. */
  heading?: number | null;
  /** Reported accuracy in metres (95% radius) when the fix carried it.
   *  null means the platform did not report one — which contribution paths
   *  must treat as FAILING the accuracy gate, not passing it. (R1) */
  accuracy?: number | null;
  /** UI-ready, non-technical message for banners/toasts. */
  message?: string;
  /** Underlying browser error code if any. */
  code?: number;
}

export const isIOS = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Modern iPadOS reports as Macintosh + touch — include that case.
  return /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Macintosh") && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);
};

export const isStandalonePWA = (): boolean => {
  if (typeof window === "undefined") return false;
  const navStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  const mqStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches === true;
  return navStandalone || mqStandalone;
};

/** Best-effort permission read. On native this asks the Capacitor plugin and
 *  is fully reliable; on web it falls back to the Permissions API and returns
 *  "unknown" on iOS Safari (which does not expose Permissions for geolocation). */
export const readGeolocationPermission = async (): Promise<"granted" | "denied" | "prompt" | "unknown"> => {
  if (isNativeRuntime()) {
    // READ only. This used to call ensurePermission, which REQUESTS on
    // "prompt" — so useUserLocation's mount-time read put the iOS dialog on
    // a fresh install's sign-in screen, uninvited (PLE-13). The request now
    // happens only on the tap path: requestUserLocation → getOneShotLocation
    // → ensurePermission.
    return checkPermission();
  }
  try {
    if (typeof navigator === "undefined" || !("permissions" in navigator)) return "unknown";
    // Some browsers throw on unknown name; guard with try.
    const res = await (navigator.permissions as Permissions).query({ name: "geolocation" as PermissionName });
    return res.state as "granted" | "denied" | "prompt";
  } catch {
    return "unknown";
  }
};

const messageForCode = (code: number, fallback: string): string => {
  switch (code) {
    case 1: { // PERMISSION_DENIED
      // The native iOS shell (Capacitor) reads location permission from
      // iPhone Settings → Pleasant Routes → Location, NOT from Safari.
      // The previous message told testers to fix it via Safari settings —
      // which is correct for the web build but wrong for the App Store
      // build they actually have on their phone. Real-drive note (#55):
      // a tester following the Safari path would never find the toggle.
      if (isNativeRuntime()) {
        return "Location is off for Pleasant Routes. To turn it on: open iPhone Settings → Pleasant Routes → Location → While Using the App.";
      }
      return isIOS()
        ? "Location is off for this site. To turn it on: open iPhone Settings → Safari → Location → Allow."
        : "Location access was blocked. Update your browser's site settings to allow location for Pleasant Routes.";
    }
    case 2: // POSITION_UNAVAILABLE
      return "We couldn't read your location right now. Try moving to an area with better signal, or set a starting point manually.";
    case 3: // TIMEOUT
      return "Locating took too long. You can try again or type a starting point.";
    default:
      return fallback;
  }
};

interface RequestOptions {
  enableHighAccuracy?: boolean;
  timeoutMs?: number;
  maximumAgeMs?: number;
}

/** One-shot location request with friendly result envelope. Never throws.
 *  Uses the native plugin when available, navigator.geolocation otherwise. */
export const requestUserLocation = async (opts: RequestOptions = {}): Promise<GeolocationResult> => {
  // Native shell: route through Capacitor.
  if (isNativeRuntime()) {
    const perm = await readGeolocationPermission();
    if (perm === "denied") {
      return { status: "denied", message: messageForCode(1, "Location is blocked."), code: 1 };
    }
    try {
      const fix = await getOneShotLocation({
        enableHighAccuracy: opts.enableHighAccuracy ?? true,
        timeoutMs: opts.timeoutMs ?? 12_000,
        maximumAgeMs: opts.maximumAgeMs ?? 60_000,
      });
      return {
        status: "granted",
        coords: [fix.lng, fix.lat] as LngLat,
        speed: typeof fix.speed === "number" ? fix.speed : null,
        heading: typeof fix.heading === "number" ? fix.heading : null,
        accuracy: typeof fix.accuracy === "number" ? fix.accuracy : null,
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not get your location.";
      const denied = /denied|permission/i.test(msg);
      // PUMP RESCUE (Morgan, Aug 8, Umbria: phone AND car said no location
      // while Apple Maps had a fix). The webview geolocation plugin can
      // time out independently of the OS — but our Swift location pump
      // streams real fixes the whole time a car is connected, and this
      // function never asked it. On any non-permission failure, take the
      // pump's latest fix if it's recent enough for the caller's own
      // freshness budget (fallback cap: 2 minutes).
      if (!denied) {
        try {
          const { lastPumpFix } = await import("@/native/carplayBridge");
          const pump = lastPumpFix();
          const budget = Math.max(opts.maximumAgeMs ?? 60_000, 120_000);
          if (pump && pump.ageMs < budget) {
            console.log(`[geo] plugin failed — pump rescue (fix ${Math.round(pump.ageMs / 1000)}s old)`);
            // The pump does not carry accuracy, so it stays null — a
            // contribution cannot auto-resolve from a pump-rescued fix.
            return { status: "granted", coords: pump.coords, speed: null, heading: null, accuracy: null };
          }
        } catch { /* no pump on this platform */ }
      }
      return {
        status: denied ? "denied" : "error",
        message: denied ? messageForCode(1, msg) : msg,
        code: denied ? 1 : undefined,
      };
    }
  }

  // Web fallback.
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
    return {
      status: "unavailable",
      message: "This device or browser can't share its location. You can still plan a trip by typing a starting point.",
    };
  }

  const perm = await readGeolocationPermission();
  if (perm === "denied") {
    return {
      status: "denied",
      message: messageForCode(1, "Location is blocked for this site."),
      code: 1,
    };
  }

  const enableHighAccuracy = opts.enableHighAccuracy ?? true;
  const timeout = opts.timeoutMs ?? 12_000;
  const maximumAge = opts.maximumAgeMs ?? 60_000;

  return new Promise<GeolocationResult>((resolve) => {
    let settled = false;
    const finish = (r: GeolocationResult) => {
      if (settled) return;
      settled = true;
      resolve(r);
    };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          finish({
            status: "granted",
            coords: [pos.coords.longitude, pos.coords.latitude] as LngLat,
            speed: typeof pos.coords.speed === "number" ? pos.coords.speed : null,
            heading: typeof pos.coords.heading === "number" ? pos.coords.heading : null,
            accuracy: typeof pos.coords.accuracy === "number" ? pos.coords.accuracy : null,
          });
        },
        (err) => {
          const status: GeolocationStatus =
            err.code === 1 ? "denied" : err.code === 3 ? "timeout" : "error";
          finish({
            status,
            code: err.code,
            message: messageForCode(err.code, err.message || "Could not get your location."),
          });
        },
        { enableHighAccuracy, timeout, maximumAge },
      );
    } catch (e) {
      finish({
        status: "error",
        message: e instanceof Error ? e.message : "Location request failed.",
      });
    }
  });
};
